import _, { isEmpty } from "lodash";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";

import { generateMessage, handleResErrors } from "./utils";
import { setDisableBtn, toggleDialog } from "../redux/Slices/Global/globalSlice";
import { setSelectedCampaign, setSelectedDevice, setSelectedGroup, setShowAssignForm } from "../redux/Slices/deviceSlice";
import { useCustomToast } from "../pages/Contexts/ToastContext";
import axiosInstance from "../config/Axios";
import { useHoldGuard, whereOfAssign } from "../schedule/holdGuard";

//--------------------------Get Campaign By ID--------------------------
const getDeviceScheduleCampaigns = async (id) => {
    const { data } = await axiosInstance.get(`/campaign/device/${id}/show`);
    return data.data;
}

export const useGetDeviceScheduleCampaigns = () => {
    const { showToast } = useCustomToast();
    const selectedDevice = useSelector(state => state.device.selectedDevice)

    const { isLoading, data, error, isError, refetch } = useQuery(
        ['getDeviceScheduleCampaigns', selectedDevice?.id],
        () => getDeviceScheduleCampaigns(selectedDevice?.id),
        {
            onError: (error) => {
                showToast("error", "Fetching Data", error.response?.data?.message);
            },
        }
    );

    return { isLoading, data, error, isError, refetch };
}

//--------------------------Assign Event Campaign Devices--------------------------
const assignCampaignDevices = async (payload) => {
    const { data } = await axiosInstance.post("/campaign/assign/devices", payload);
    return data?.data;
}

export const useAssignCampaignDevices = () => {
    const selectedDevice = useSelector(state => state.device.selectedDevice)
    const { showToast } = useCustomToast();
    const dispatch = useDispatch()
    // schedule v4: a change by hand while a schedule window is playing asks first (nothing playing = no dialog, as before)
    const ensureHold = useHoldGuard();

    const mutation = useMutation(async (payload) => {
        await ensureHold(whereOfAssign(payload));
        return assignCampaignDevices(payload);
    }, {
        onSuccess: async (data) => {
            let rejectedSatellite = data?.rejected?.satellite;
            var errorsArr = [];

            if (_.isEmpty(rejectedSatellite)) {
                showToast('success', 'Assign Campaign', data?.msg);
                if (!_.isEmpty(selectedDevice))
                    dispatch(setShowAssignForm(false))
            }

            if (!_.isEmpty(rejectedSatellite)) {
                errorsArr.push(generateMessage(rejectedSatellite, "satellite"));
            }

            errorsArr?.map((error) => { showToast(error.severity, error.summary, error.detail) })

        },
        onError: (error) => {
            if (error?.holdCancelled) return; // the person cancelled the schedule-pause dialog: nothing was sent
            handleResErrors(error, showToast)
        }
    })

    // callers `await mutateAsync(...)`: a cancelled dialog is not an error for them
    return { ...mutation, mutateAsync: (...args) => mutation.mutateAsync(...args).catch((e) => { if (e?.holdCancelled) return undefined; throw e; }) };
}

//--------------------------Assign Schedule Campaign to Group--------------------------
const assignScheduleCampaign = async (payload) => {
    const { data } = await axiosInstance.post("/groups/assign/campaign/schedule", payload);
    return data?.data;
}

export const useAssignScheduleCampaign = () => {
    const { showToast } = useCustomToast();
    const dispatch = useDispatch()

    return useMutation(assignScheduleCampaign, {
        onSuccess: async (responseData) => {
            dispatch(setDisableBtn(false));

            if (responseData?.error === "HDMI") {
                showToast("error", "Assign Campaign", responseData?.msg);
            } else if (_.isEmpty(responseData?.error)) {
                dispatch(toggleDialog("assignDialog"));
                showToast("success", "Assign Campaign", responseData?.msg);
                dispatch(setSelectedGroup({}));
                dispatch(setSelectedCampaign(""));


            }
        },
        onError: (error) => {

            handleResErrors(error, showToast)
        }
    })
}

//--------------------------Replace and Assign Schedule Campaign to Group--------------------------
const replaceScheduleCampaign = async (payload) => {
    const { data } = await axiosInstance.post("/groups/schedule/campaign/replace", payload);
    return data?.data;
}

export const useReplaceScheduleCampaign = () => {
    const { showToast } = useCustomToast();
    const openDialog = useSelector(state => state.global.openDialog)
    const dispatch = useDispatch()

    return useMutation(replaceScheduleCampaign, {
        onSuccess: async (responseData) => {
            showToast('success', 'Assign Campaign', responseData?.msg);
            dispatch(setSelectedGroup({}));
            dispatch(setSelectedCampaign(""));

            dispatch(toggleDialog("campaignConflictDialog"))

            if (openDialog?.assignDialog)
                dispatch(toggleDialog("assignDialog"))

            if (openDialog?.assignScheduleCampaigns)
                dispatch(toggleDialog("assignScheduleCampaigns"))
        },
        onError: (error) => {
            handleResErrors(error, showToast)
        }
    })
}

//-------------------------- Assign Schedule Campaign And Ignore Devices with Conflict--------------------------
const ignoreDevicesWithConflict = async (payload) => {
    const { data } = await axiosInstance.post("/groups/schedule/campaign/rest", payload);
    return data?.data;
}

export const useIgnoreDevicesWithConflict = () => {
    const { showToast } = useCustomToast();
    const dispatch = useDispatch()

    return useMutation(ignoreDevicesWithConflict, {
        onSuccess: async (responseData) => {
            showToast('success', 'Assign Campaign', responseData?.msg);
            dispatch(setSelectedGroup({}));
            dispatch(setSelectedCampaign(""));

            dispatch(toggleDialog("campaignConflictDialog"))
            dispatch(toggleDialog("assignDialog"))
        },
        onError: (error) => {
            handleResErrors(error, showToast)
        }
    })
}

//-------------------------- Assign Schedule Campaign to Device--------------------------
const assignScheduleCampaignDevice = async (payload) => {
    const { data } = await axiosInstance.post("/campaign/device/assign", payload);
    return data?.data;
}

export const useScheduleCampaignDevice = () => {
    const { showToast } = useCustomToast();
    const dispatch = useDispatch()
    const queryClient = useQueryClient();
    const openDialog = useSelector(state => state.global.openDialog)

    return useMutation(assignScheduleCampaignDevice, {
        onSuccess: async (responseData) => {
            if (isEmpty(responseData.error)) {
                queryClient.invalidateQueries('getDeviceScheduleCampaigns');
                showToast('success', 'Assign Campaign', responseData?.msg);
                if (openDialog.takeOverScreen) {
                    dispatch(toggleDialog("takeOverScreen"))
                }
                dispatch(setShowAssignForm(false))
            }

        },
        onError: (error) => {
            handleResErrors(error, showToast)
        }
    })
}


//--------------------------Replace and Assign Schedule Campaign to device--------------------------
const replaceScheduleCampaignDevice = async (payload) => {
    const { data } = await axiosInstance.post("/campaign/device/replace", payload);
    return data?.data;
}

export const useReplaceScheduleCampaignDevice = () => {
    const { showToast } = useCustomToast();
    const openDialog = useSelector(state => state.global.openDialog)
    const dispatch = useDispatch()
    const queryClient = useQueryClient();

    return useMutation(replaceScheduleCampaignDevice, {
        onSuccess: async (responseData) => {
            showToast('success', 'Assign Campaign', responseData?.msg);
            queryClient.invalidateQueries('getDeviceScheduleCampaigns');
            dispatch(toggleDialog("campaignConflictDialog"))
            if (openDialog.takeOverScreen) {
                dispatch(toggleDialog("takeOverScreen"))
            }
            dispatch(setShowAssignForm(false))
        },
        onError: (error) => {
            handleResErrors(error, showToast)
        }
    })
}


//-------------------------- Delete Schedule Campaign--------------------------
const deleteScheduleCampaign = async (payload) => {
    const { data } = await axiosInstance.delete(`/campaign/device/${payload?.id}/delete`);
    return data?.data;
}

export const useDeleteScheduleCampaign = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();

    return useMutation(deleteScheduleCampaign, {
        onSuccess: async (responseData) => {
            queryClient.invalidateQueries('getDeviceScheduleCampaigns');
            showToast('success', 'Delete Schedule Campaign', responseData?.msg);
        },
        onError: (error) => {
            handleResErrors(error, showToast)
        }
    })
}
